import React, { useEffect, useMemo, useState } from "react";
import { courseList } from "../../../api/Courses";
import {
    batchDetailsWithStudents,
    batchList,
    saveStudentSubjectMapping,
} from "../../../api/Batch";
import { LoadingDiv, SweetAlertShow } from "../../master/Master";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import Master from "../Master";

/*
|--------------------------------------------------------------------------
| Normalize Subjects
|--------------------------------------------------------------------------
*/

const normalizeSubjects = (value) => {
    if (!value) return [];

    let parsed = value;

    for (let i = 0; i < 4; i++) {
        if (typeof parsed !== "string") break;

        try {
            parsed = JSON.parse(parsed);
        } catch {
            break;
        }
    }

    if (!Array.isArray(parsed)) return [];

    const result = [];

    parsed.forEach((item) => {
        let subject = item;

        if (typeof subject === "string") {
            try {
                subject = JSON.parse(subject);
            } catch {
                return;
            }
        }

        if (
            subject &&
            typeof subject === "object" &&
            subject.id !== undefined
        ) {
            result.push({
                id: Number(subject.id),
                status: subject.status ?? "1",
                name:
                    subject.name ||
                    subject.title ||
                    subject.subject_name ||
                    "-",
                subject_code:
                    subject.subject_code ||
                    subject.code ||
                    "",
            });
        }
    });

    return result.filter(
        (subject, index, self) =>
            index ===
            self.findIndex(
                (item) =>
                    Number(item.id) === Number(subject.id)
            )
    );
};

/*
|--------------------------------------------------------------------------
| Get Course Subjects
|--------------------------------------------------------------------------
*/

const getCourseSubjects = (course) => {
    if (!course) return [];

    const values = [
        course.subjects,
        course.course_subjects,
        course.course_subject,
        course.subject_list,
        course.subjects_list,
        course.subjectList,
    ];

    for (const value of values) {
        const subjects = normalizeSubjects(value);

        if (subjects.length) {
            return subjects;
        }
    }

    return [];
};

export default function StudentSubjectMapping() {

    const [loading, setLoading] = useState(false);

    const [sessions, setSessions] = useState([]);
    const [courses, setCourses] = useState([]);
    const [batches, setBatches] = useState([]);

    const [selectedSession, setSelectedSession] = useState("");
    const [selectedCourse, setSelectedCourse] = useState("");
    const [selectedBatch, setSelectedBatch] = useState("");

    const [students, setStudents] = useState([]);
    const [subjects, setSubjects] = useState([]);

    /*
     * {
     *   studentBatchId: [
     *      { id: 1, name: "Maths" },
     *      { id: 2, name: "Science" }
     *   ]
     * }
     */
    const [studentSubjects, setStudentSubjects] = useState({});

    const [selectedStudents, setSelectedStudents] = useState([]);

    const [search, setSearch] = useState("");

    /*
    |--------------------------------------------------------------------------
    | Load Sessions
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        try {
            const data = JSON.parse(
                localStorage.getItem("sessions") ?? "[]"
            );

            const sessionList = Array.isArray(data)
                ? data
                : [];

            setSessions(sessionList);

            const activeSession = sessionList.find(
                (item) => Number(item.status) === 1
            );

            if (activeSession) {
                setSelectedSession(
                    String(activeSession.id)
                );
            }

        } catch (error) {
            console.error(
                "Session load error:",
                error
            );
        }
    }, []);

    /*
    |--------------------------------------------------------------------------
    | Load Courses
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        getCourses();
    }, []);

    const getCourses = async () => {

        try {

            setLoading(true);

            const response = await courseList();

            if (response?.data?.status) {

                setCourses(
                    Array.isArray(
                        response?.data?.data
                    )
                        ? response.data.data
                        : []
                );

            } else {

                setCourses([]);

                SweetAlertShow(
                    "info",
                    "Warning",
                    response?.data?.message ||
                    "Unable to load courses"
                );
            }

        } catch (error) {

            setCourses([]);

            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong"
            );

        } finally {

            setLoading(false);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Course Changed
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        if (!selectedCourse) {

            setBatches([]);
            setSelectedBatch("");

            setStudents([]);
            setSubjects([]);

            setStudentSubjects({});
            setSelectedStudents([]);

            return;
        }

        getBatches(selectedCourse);

    }, [selectedCourse]);

    /*
    |--------------------------------------------------------------------------
    | Get Batches
    |--------------------------------------------------------------------------
    */

    const getBatches = async (courseId) => {

        try {

            setLoading(true);

            const response = await batchList(
                `course_id=${courseId}`
            );

            if (response?.data?.status) {

                setBatches(
                    Array.isArray(
                        response?.data?.data
                    )
                        ? response.data.data
                        : []
                );

            } else {

                setBatches([]);

                SweetAlertShow(
                    "info",
                    "Warning",
                    response?.data?.message ||
                    "Unable to load batches"
                );
            }

        } catch (error) {

            setBatches([]);

            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong"
            );

        } finally {

            setLoading(false);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Batch / Session Changed
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        if (
            !selectedSession ||
            !selectedBatch
        ) {

            setStudents([]);
            setStudentSubjects({});
            setSelectedStudents([]);

            return;
        }

        getBatchStudents();

    }, [
        selectedSession,
        selectedBatch,
    ]);

    /*
    |--------------------------------------------------------------------------
    | Get Batch Students
    |--------------------------------------------------------------------------
    */

    const getBatchStudents = async () => {

        try {

            setLoading(true);

            const response =
                await batchDetailsWithStudents(
                    `&id=${selectedBatch}&status=1`
                );

            if (!response?.data?.status) {

                setStudents([]);
                setStudentSubjects({});

                SweetAlertShow(
                    "info",
                    "Warning",
                    response?.data?.message ||
                    "Unable to load students"
                );

                return;
            }

            const batchData =
                response?.data?.data || {};

            const activeStudents =
                Array.isArray(
                    batchData?.active_batch_students
                )
                    ? batchData.active_batch_students
                    : [];

            setStudents(activeStudents);

            /*
            |--------------------------------------------------------------------------
            | Course Subjects
            |--------------------------------------------------------------------------
            */

            const selectedCourseObject =
                courses.find(
                    (course) =>
                        Number(course.id) ===
                        Number(selectedCourse)
                );

            let courseSubjects =
                getCourseSubjects(
                    selectedCourseObject
                );

            if (!courseSubjects.length) {

                courseSubjects =
                    getCourseSubjects(
                        batchData?.course
                    );
            }

            if (!courseSubjects.length) {

                courseSubjects =
                    normalizeSubjects(
                        batchData?.subjects
                    );
            }

            setSubjects(courseSubjects);

            /*
            |--------------------------------------------------------------------------
            | Existing Student Subject Mapping
            |--------------------------------------------------------------------------
            */

            const mapped = {};

            activeStudents.forEach((student) => {

                const existingSubjects =
                    normalizeSubjects(
                        student?.subjects
                    );

                mapped[student.id] =
                    existingSubjects.filter(
                        (existing) =>
                            courseSubjects.some(
                                (courseSubject) =>
                                    Number(
                                        courseSubject.id
                                    ) ===
                                    Number(
                                        existing.id
                                    )
                            )
                    );
            });

            setStudentSubjects(mapped);

            setSelectedStudents([]);

        } catch (error) {

            console.error(
                "Batch students error:",
                error
            );

            setStudents([]);
            setSubjects([]);
            setStudentSubjects({});

            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong"
            );

        } finally {

            setLoading(false);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Search Students
    |--------------------------------------------------------------------------
    */

    const filteredStudents = useMemo(() => {

        if (!search.trim()) {
            return students;
        }

        const value =
            search.toLowerCase().trim();

        return students.filter(
            (student) => {

                const name =
                    student?.student_info?.name ||
                    student?.name ||
                    "";

                const registration =
                    student?.student_info
                        ?.registration_no ||
                    student?.registration_no ||
                    "";

                const father =
                    student?.student_info
                        ?.father_name ||
                    student?.father_name ||
                    "";

                const roll =
                    student?.roll_number ||
                    student?.roll_no ||
                    "";

                return (
                    String(name)
                        .toLowerCase()
                        .includes(value) ||

                    String(registration)
                        .toLowerCase()
                        .includes(value) ||

                    String(father)
                        .toLowerCase()
                        .includes(value) ||

                    String(roll)
                        .toLowerCase()
                        .includes(value)
                );
            }
        );

    }, [students, search]);

    /*
    |--------------------------------------------------------------------------
    | Select / Unselect Student
    |--------------------------------------------------------------------------
    */

    const toggleStudent = (studentId) => {

        setSelectedStudents((prev) => {

            if (prev.includes(studentId)) {

                return prev.filter(
                    (id) => id !== studentId
                );
            }

            return [
                ...prev,
                studentId,
            ];
        });
    };

    /*
    |--------------------------------------------------------------------------
    | Select / Unselect All Students
    |--------------------------------------------------------------------------
    */

    const toggleAllStudents = () => {

        const filteredIds =
            filteredStudents.map(
                (student) => student.id
            );

        const allSelected =
            filteredIds.length > 0 &&
            filteredIds.every(
                (id) =>
                    selectedStudents.includes(id)
            );

        if (allSelected) {

            setSelectedStudents(
                (prev) =>
                    prev.filter(
                        (id) =>
                            !filteredIds.includes(id)
                    )
            );

        } else {

            setSelectedStudents(
                (prev) => [
                    ...new Set([
                        ...prev,
                        ...filteredIds,
                    ]),
                ]
            );
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Toggle Subject
    |--------------------------------------------------------------------------
    */

    const toggleSubject = (
        studentId,
        subject
    ) => {

        setStudentSubjects((prev) => {

            const current =
                Array.isArray(
                    prev[studentId]
                )
                    ? prev[studentId]
                    : [];

            const exists =
                current.some(
                    (item) =>
                        Number(item.id) ===
                        Number(subject.id)
                );

            let updated;

            if (exists) {

                updated =
                    current.filter(
                        (item) =>
                            Number(item.id) !==
                            Number(subject.id)
                    );

            } else {

                updated = [
                    ...current,
                    {
                        id: Number(subject.id),
                        status:
                            subject.status ??
                            "1",
                        name:
                            subject.name,
                        subject_code:
                            subject.subject_code,
                    },
                ];
            }

            return {
                ...prev,
                [studentId]: updated,
            };
        });
    };

    /*
    |--------------------------------------------------------------------------
    | Select All Subjects For Student
    |--------------------------------------------------------------------------
    */

    const selectAllSubjectsForStudent = (
        studentId
    ) => {

        setStudentSubjects((prev) => {

            const current =
                Array.isArray(
                    prev[studentId]
                )
                    ? prev[studentId]
                    : [];

            const allSelected =
                subjects.length > 0 &&
                current.length ===
                subjects.length;

            return {
                ...prev,

                [studentId]:
                    allSelected
                        ? []
                        : subjects.map(
                            (subject) => ({
                                id: Number(
                                    subject.id
                                ),
                                status:
                                    subject.status ??
                                    "1",
                                name:
                                    subject.name,
                                subject_code:
                                    subject.subject_code,
                            })
                        ),
            };
        });
    };

    /*
    |--------------------------------------------------------------------------
    | SAVE
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    |
    | Frontend sends ONLY:
    |
    | student_batch_id
    | student_id
    | subject_ids
    |
    | No subject name/status/code.
    |
    |--------------------------------------------------------------------------
    */

    const handleSaveAll = async () => {

        if (!selectedBatch) {

            SweetAlertShow(
                "info",
                "Select Batch",
                "Please select a batch first."
            );

            return;
        }

        if (!students.length) {

            SweetAlertShow(
                "info",
                "No Students",
                "No active students found."
            );

            return;
        }

        if (
            selectedStudents.length === 0
        ) {

            SweetAlertShow(
                "info",
                "Select Student",
                "Please select at least one student to update."
            );

            return;
        }

        try {

            setLoading(true);

            /*
            |--------------------------------------------------------------------------
            | Selected Students Only
            |--------------------------------------------------------------------------
            */

            const selectedStudentData =
                students.filter(
                    (student) =>
                        selectedStudents.includes(
                            student.id
                        )
                );

            /*
            |--------------------------------------------------------------------------
            | SMALL PAYLOAD
            |--------------------------------------------------------------------------
            */

            const payload = {

                batch_id:
                    Number(selectedBatch),

                session_id:
                    Number(selectedSession),

                course_id:
                    Number(selectedCourse),

                students:
                    selectedStudentData.map(
                        (student) => ({

                            student_batch_id:
                                Number(student.id),

                            student_id:
                                Number(
                                    student.student_id ||
                                    student?.student_info?.id ||
                                    0
                                ),

                            /*
                            |--------------------------------------------------------------------------
                            | ONLY SUBJECT IDS
                            |--------------------------------------------------------------------------
                            */

                            subject_ids:
                                (
                                    studentSubjects[
                                    student.id
                                    ] || []
                                ).map(
                                    (subject) =>
                                        Number(
                                            subject.id
                                        )
                                ),
                        })
                    ),
            };

            /*
            |--------------------------------------------------------------------------
            | Debug
            |--------------------------------------------------------------------------
            */

            console.log(
                "================================"
            );

            console.log(
                "STUDENT SUBJECT MAPPING PAYLOAD"
            );

            console.log(payload);

            console.log(
                "Students:",
                payload.students.length
            );

            console.log(
                "Payload Size:",
                (
                    JSON.stringify(payload)
                        .length / 1024
                ).toFixed(2),
                "KB"
            );

            console.log(
                "================================"
            );

            /*
            |--------------------------------------------------------------------------
            | API
            |--------------------------------------------------------------------------
            */

            const response =
                await saveStudentSubjectMapping(
                    payload
                );

            if (
                response?.data?.status
            ) {

                SweetAlertShow(
                    "success",
                    "Success",
                    response?.data?.message ||
                    "Student subject mapping updated successfully."
                );

                /*
                |--------------------------------------------------------------------------
                | Reload
                |--------------------------------------------------------------------------
                */

                await getBatchStudents();

            } else {

                SweetAlertShow(
                    "info",
                    "Warning",
                    response?.data?.message ||
                    "Unable to update subject mapping."
                );
            }

        } catch (error) {

            console.error(
                "Subject mapping save error:",
                error
            );

            console.error(
                "API Response:",
                error?.response?.data
            );

            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong"
            );

        } finally {

            setLoading(false);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | All Selected
    |--------------------------------------------------------------------------
    */

    const allFilteredStudentsSelected =
        filteredStudents.length > 0 &&
        filteredStudents.every(
            (student) =>
                selectedStudents.includes(
                    student.id
                )
        );

    /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

    return (
        <>
            {loading && <LoadingDiv />}

            <div className="layout-wrapper layout-content-navbar">

                <div className="layout-container">

                    <Header />

                    <div className="layout-page">

                        <Nav />

                        <div className="content-wrapper">

                            <div className="container-fluid flex-grow-1 container-p-y">

                                <div className="row">
                                    <Master />
                                </div>

                                {/* HEADER */}

                                <div className="card p-3 mb-3 shadow-sm border-0">

                                    <div className="d-flex justify-content-between align-items-center">

                                        <div className="d-flex align-items-center gap-3">

                                            <div
                                                style={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius:
                                                        "12px",
                                                    background:
                                                        "linear-gradient(45deg,#185fa5,#4f8cff)",
                                                    color:
                                                        "#fff",
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    justifyContent:
                                                        "center",
                                                }}
                                            >
                                                <i className="ri-list-check-2 fs-4"></i>
                                            </div>

                                            <div>

                                                <h5 className="m-0 fw-bold">
                                                    Student Subject Mapping
                                                </h5>

                                                <small className="text-muted">
                                                    Select students and assign
                                                    their subjects
                                                </small>

                                            </div>

                                        </div>

                                        <div className="d-flex gap-2">

                                            <span className="badge bg-primary-subtle text-primary px-3 py-2">

                                                Students:
                                                {" "}
                                                {students.length}

                                            </span>

                                            <span className="badge bg-success-subtle text-success px-3 py-2">

                                                Subjects:
                                                {" "}
                                                {subjects.length}

                                            </span>

                                        </div>

                                    </div>

                                </div>

                                {/* FILTERS */}

                                <div className="card shadow-sm border-0 mb-3">

                                    <div className="card-body">

                                        <div className="row g-3">

                                            {/* SESSION */}

                                            <div className="col-md-4">

                                                <div className="form-floating form-floating-outline">

                                                    <select
                                                        className="form-select"
                                                        value={
                                                            selectedSession
                                                        }
                                                        onChange={(
                                                            e
                                                        ) => {

                                                            setSelectedSession(
                                                                e.target.value
                                                            );

                                                            setSelectedBatch(
                                                                ""
                                                            );

                                                            setStudents(
                                                                []
                                                            );

                                                            setSubjects(
                                                                []
                                                            );

                                                            setStudentSubjects(
                                                                {}
                                                            );

                                                            setSelectedStudents(
                                                                []
                                                            );
                                                        }}
                                                    >

                                                        <option value="">
                                                            Select Session
                                                        </option>

                                                        {sessions.map(
                                                            (
                                                                session
                                                            ) => (

                                                                <option
                                                                    key={
                                                                        session.id
                                                                    }
                                                                    value={
                                                                        session.id
                                                                    }
                                                                >

                                                                    {
                                                                        session.session
                                                                    }

                                                                    {Number(
                                                                        session.status
                                                                    ) ===
                                                                        1
                                                                        ? " : Active"
                                                                        : " : Inactive"}

                                                                </option>

                                                            )
                                                        )}

                                                    </select>

                                                    <label>
                                                        Session
                                                    </label>

                                                </div>

                                            </div>

                                            {/* COURSE */}

                                            <div className="col-md-4">

                                                <div className="form-floating form-floating-outline">

                                                    <select
                                                        className="form-select"
                                                        value={
                                                            selectedCourse
                                                        }
                                                        onChange={(
                                                            e
                                                        ) => {

                                                            setSelectedCourse(
                                                                e.target.value
                                                            );

                                                            setSelectedBatch(
                                                                ""
                                                            );

                                                            setStudents(
                                                                []
                                                            );

                                                            setSubjects(
                                                                []
                                                            );

                                                            setStudentSubjects(
                                                                {}
                                                            );

                                                            setSelectedStudents(
                                                                []
                                                            );
                                                        }}
                                                    >

                                                        <option value="">
                                                            Select Course / Class
                                                        </option>

                                                        {courses.map(
                                                            (
                                                                course
                                                            ) => (

                                                                <option
                                                                    key={
                                                                        course.id
                                                                    }
                                                                    value={
                                                                        course.id
                                                                    }
                                                                >

                                                                    {
                                                                        course.title
                                                                    }

                                                                </option>

                                                            )
                                                        )}

                                                    </select>

                                                    <label>
                                                        Course / Class
                                                    </label>

                                                </div>

                                            </div>

                                            {/* BATCH */}

                                            <div className="col-md-4">

                                                <div className="form-floating form-floating-outline">

                                                    <select
                                                        className="form-select"
                                                        value={
                                                            selectedBatch
                                                        }
                                                        disabled={
                                                            !selectedCourse
                                                        }
                                                        onChange={(
                                                            e
                                                        ) =>
                                                            setSelectedBatch(
                                                                e.target.value
                                                            )
                                                        }
                                                    >

                                                        <option value="">
                                                            Select Batch
                                                        </option>

                                                        {batches.map(
                                                            (
                                                                batch
                                                            ) => (

                                                                <option
                                                                    key={
                                                                        batch.id
                                                                    }
                                                                    value={
                                                                        batch.id
                                                                    }
                                                                >

                                                                    {
                                                                        batch.title
                                                                    }

                                                                </option>

                                                            )
                                                        )}

                                                    </select>

                                                    <label>
                                                        Batch
                                                    </label>

                                                </div>

                                            </div>

                                        </div>

                                    </div>

                                </div>

                                {/* STUDENTS */}

                                {selectedBatch && (

                                    <div className="card shadow-sm border-0">

                                        <div className="card-header bg-white border-bottom">

                                            <div className="row align-items-center g-2">

                                                <div className="col-md-4">

                                                    <h5 className="mb-0">
                                                        Students
                                                    </h5>

                                                    <small className="text-muted">
                                                        Select subjects for each
                                                        student
                                                    </small>

                                                </div>

                                                {/* SEARCH */}

                                                <div className="col-md-4">

                                                    <div className="form-floating form-floating-outline">

                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Search"
                                                            value={
                                                                search
                                                            }
                                                            onChange={(
                                                                e
                                                            ) =>
                                                                setSearch(
                                                                    e.target.value
                                                                )
                                                            }
                                                        />

                                                        <label>
                                                            Search Student
                                                        </label>

                                                    </div>

                                                </div>

                                                {/* ACTIONS */}

                                                <div className="col-md-4 text-end">

                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary me-2"
                                                        onClick={
                                                            toggleAllStudents
                                                        }
                                                    >

                                                        <i className="ri-checkbox-multiple-line me-1"></i>

                                                        {allFilteredStudentsSelected
                                                            ? "Unselect All"
                                                            : "Select All"}

                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        onClick={
                                                            handleSaveAll
                                                        }
                                                    >

                                                        <i className="ri-save-line me-1"></i>

                                                        Save All

                                                    </button>

                                                </div>

                                            </div>

                                        </div>

                                        <div className="card-body p-0">

                                            <div
                                                className="table-responsive"
                                                style={{
                                                    maxHeight:
                                                        "650px",
                                                    overflowY:
                                                        "auto",
                                                }}
                                            >

                                                <table className="table table-hover align-middle mb-0">

                                                    <thead
                                                        className="table-light"
                                                        style={{
                                                            position:
                                                                "sticky",
                                                            top: 0,
                                                            zIndex: 5,
                                                        }}
                                                    >

                                                        <tr>

                                                            <th
                                                                style={{
                                                                    width: 50,
                                                                }}
                                                            >

                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    checked={
                                                                        allFilteredStudentsSelected
                                                                    }
                                                                    onChange={
                                                                        toggleAllStudents
                                                                    }
                                                                />

                                                            </th>

                                                            <th>
                                                                Student
                                                            </th>

                                                            <th>
                                                                Subjects
                                                            </th>

                                                            <th>
                                                                Total
                                                            </th>

                                                        </tr>

                                                    </thead>

                                                    <tbody>

                                                        {filteredStudents.length ===
                                                            0 ? (

                                                            <tr>

                                                                <td
                                                                    colSpan="4"
                                                                    className="text-center py-5"
                                                                >

                                                                    <i className="ri-user-search-line fs-1 text-muted"></i>

                                                                    <div className="mt-2 text-muted">
                                                                        No
                                                                        students
                                                                        found
                                                                    </div>

                                                                </td>

                                                            </tr>

                                                        ) : (

                                                            filteredStudents.map(
                                                                (
                                                                    student,
                                                                    index
                                                                ) => {

                                                                    const selected =
                                                                        Array.isArray(
                                                                            studentSubjects[
                                                                            student.id
                                                                            ]
                                                                        )
                                                                            ? studentSubjects[
                                                                            student.id
                                                                            ]
                                                                            : [];

                                                                    const allSubjectsSelected =
                                                                        subjects.length >
                                                                        0 &&
                                                                        selected.length ===
                                                                        subjects.length;

                                                                    const studentInfo =
                                                                        student?.student_info ||
                                                                        {};

                                                                    return (

                                                                        <tr
                                                                            key={
                                                                                student.id
                                                                            }
                                                                        >

                                                                            {/* STUDENT CHECKBOX */}

                                                                            <td>

                                                                                <input
                                                                                    type="checkbox"
                                                                                    className="form-check-input"
                                                                                    checked={selectedStudents.includes(
                                                                                        student.id
                                                                                    )}
                                                                                    onChange={() =>
                                                                                        toggleStudent(
                                                                                            student.id
                                                                                        )
                                                                                    }
                                                                                />

                                                                            </td>

                                                                            {/* STUDENT */}

                                                                            <td>

                                                                                <div className="fw-semibold">

                                                                                    {
                                                                                        studentInfo?.name ||
                                                                                        student?.name ||
                                                                                        "-"
                                                                                    }

                                                                                    {" "}

                                                                                    (

                                                                                    {
                                                                                        studentInfo?.registration_no ||
                                                                                        student?.registration_no ||
                                                                                        "-"
                                                                                    }

                                                                                    )

                                                                                </div>

                                                                                <small className="text-muted">

                                                                                    #
                                                                                    {
                                                                                        index +
                                                                                        1
                                                                                    }

                                                                                </small>

                                                                            </td>

                                                                            {/* SUBJECTS */}

                                                                            <td>

                                                                                <div className="d-flex justify-content-between align-items-center mb-2">

                                                                                    <span className="badge bg-primary-subtle text-primary">

                                                                                        {
                                                                                            selected.length
                                                                                        }

                                                                                        {" "}
                                                                                        Selected

                                                                                    </span>

                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-sm btn-outline-primary"
                                                                                        onClick={() =>
                                                                                            selectAllSubjectsForStudent(
                                                                                                student.id
                                                                                            )
                                                                                        }
                                                                                    >

                                                                                        {allSubjectsSelected
                                                                                            ? "Unselect All"
                                                                                            : "Select All"}

                                                                                    </button>

                                                                                </div>

                                                                                <div className="d-flex flex-wrap gap-2">

                                                                                    {subjects.map(
                                                                                        (
                                                                                            subject
                                                                                        ) => {

                                                                                            const checked =
                                                                                                selected.some(
                                                                                                    (
                                                                                                        item
                                                                                                    ) =>
                                                                                                        Number(
                                                                                                            item.id
                                                                                                        ) ===
                                                                                                        Number(
                                                                                                            subject.id
                                                                                                        )
                                                                                                );

                                                                                            return (

                                                                                                <label
                                                                                                    key={
                                                                                                        subject.id
                                                                                                    }
                                                                                                    className={`border rounded px-2 py-1 d-flex align-items-center gap-2 ${checked
                                                                                                            ? "border-primary bg-primary-subtle"
                                                                                                            : "bg-light"
                                                                                                        }`}
                                                                                                    style={{
                                                                                                        cursor:
                                                                                                            "pointer",
                                                                                                    }}
                                                                                                >

                                                                                                    <input
                                                                                                        type="checkbox"
                                                                                                        className="form-check-input m-0"
                                                                                                        checked={
                                                                                                            checked
                                                                                                        }
                                                                                                        onChange={() =>
                                                                                                            toggleSubject(
                                                                                                                student.id,
                                                                                                                subject
                                                                                                            )
                                                                                                        }
                                                                                                    />

                                                                                                    <span>

                                                                                                        {
                                                                                                            subject.name
                                                                                                        }

                                                                                                        {subject.subject_code && (

                                                                                                            <small className="text-muted ms-1">

                                                                                                                (
                                                                                                                {
                                                                                                                    subject.subject_code
                                                                                                                }
                                                                                                                )

                                                                                                            </small>

                                                                                                        )}

                                                                                                    </span>

                                                                                                </label>

                                                                                            );
                                                                                        }
                                                                                    )}

                                                                                    {!subjects.length && (

                                                                                        <span className="text-muted">

                                                                                            No
                                                                                            subjects
                                                                                            found
                                                                                            for
                                                                                            this
                                                                                            course.

                                                                                        </span>

                                                                                    )}

                                                                                </div>

                                                                            </td>

                                                                            {/* TOTAL */}

                                                                            <td>

                                                                                <span className="badge bg-success">

                                                                                    {
                                                                                        selected.length
                                                                                    }

                                                                                </span>

                                                                            </td>

                                                                        </tr>

                                                                    );
                                                                }
                                                            )

                                                        )}

                                                    </tbody>

                                                </table>

                                            </div>

                                        </div>

                                        {/* FOOTER */}

                                        <div className="card-footer bg-white border-top">

                                            <div className="d-flex justify-content-end">

                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={
                                                        handleSaveAll
                                                    }
                                                >

                                                    <i className="ri-save-line me-1"></i>

                                                    Save All Students

                                                </button>

                                            </div>

                                        </div>

                                    </div>

                                )}

                                {/* NO BATCH */}

                                {!selectedBatch && (

                                    <div className="card border-0 shadow-sm">

                                        <div className="card-body text-center py-5">

                                            <i
                                                className="ri-user-settings-line"
                                                style={{
                                                    fontSize:
                                                        "60px",
                                                    color:
                                                        "#7367f0",
                                                }}
                                            ></i>

                                            <h5 className="mt-3">
                                                Select Session & Batch
                                            </h5>

                                            <p className="text-muted mb-0">
                                                Select a session and batch to
                                                load students.
                                            </p>

                                        </div>

                                    </div>

                                )}

                            </div>

                        </div>

                    </div>

                </div>

            </div>
        </>
    );
}